import ViewModel from '@/ViewModels/ViewModel';
import Principal from '@/Model/Principal';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import { ILoader } from '@/Utility/Loader';
import Profiler from '@/Utility/Profiler';
import ComponentManager from '@/Management/ComponentManager';
import type { IConfigurationDTO } from "coreframetypings/dto";
import PrincipalHelper from '@/Utility/PrincipalHelper';
import Config from '@/Management/GlobalConfiguration';
import UIInterceptor from '@/UIInterceptors/UIInterceptor';
import MessageBoxViewModelT from '@/ViewModels/Controls/MessageBoxViewModel';
import type { IDropdownMenuItem } from '#/CoreFrameInterfaces';
import { IResourceManager } from '@/Management/ResourceManager';
import { INavigationService } from '@/Management/NavigationService';
import Utilities from '@/Utility/Utilities';
import StorageHelper from '@/Utility/StorageHelper';
import type { IKeepAliveHandler } from '@/Management/KeepAliveHandler';
import { IRADEnvironmentProxy } from '@/Communication/RADEnvironmentProxy';
import Messenger from '@/Utility/Messenger';
import { inject, singleton } from "tsyringe";
import { IUserProvider } from "@/Infrastructure/UserProvider";
import { IRADApplication } from "@/Management/RADApplication";
import User from "@/Model/User";

/**
 * Repräsentiert die RAD-Anwendung
 * Initialisiert wichtige Komponenten wie ResourceManager und NavigationService und beinhaltet Informationen über die Anwendung
 */
@singleton()
export class RADApplicationImpl implements IRADApplication {

    /**
     * Der Name der Anwendung
     */
    appName: KnockoutObservable<string>;

    /**
     * Die Beschreibung der Anwendung
     */
    description: KnockoutObservable<string>;

    /**
     * Der ergonomische Bezeichner der Anwendung
     */
    ergName: KnockoutObservable<string>;

    /**
     * Die Build-Version der Anwendung
     */
    buildVersion: KnockoutObservable<string>;

    /**
     * Das ViewModel des Wireframes
     */
    rootLayoutVM: ViewModel;

    /**
     * Gibt an, ob die Anwendung derzeit gesperrt ist
     */
    isSystemLocked: KnockoutObservable<boolean>;

    /**
     * Der Zeitpunkt, an dem zuletzt ein Cache-Update stattgefunden hat
     */
    lastCacheUpdate: KnockoutObservable<any>;

    /**
     * Der Zeitpunkt, an dem zuletzt ein Update des Rights-Cache stattgefunden hat
     */
    lastRightCacheUpdate: KnockoutObservable<any>;

    /**
     * Der PDTypeName der Mandanten
     */
    principalTypeName: KnockoutObservable<string>;

    /**
     * Beinhaltet den Grund der Sperrung falls eine aktive Systemsperre vorliegt
     */
    systemLockMessage: KnockoutObservable<string>;

    /**
     * Gibt an, ob gerade ein erzwungener Logout stattfindet. In diesem Fall sind beispielsweise weitere Nachfragen bei Benutzer zu unterbinden.
     */
    isForceLogout: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob die Auswahl für den Mandantenwechsel aktiviert ist.
     */
    isChangePrincipalEnabled: KnockoutObservable<boolean>;

    /**
     * Der ausgewählte Template-Style
     */
    private _templateStyle: KnockoutObservable<any>;

    /**
     * Zeigt an, ob die Warnung bzgl. abgelaufener Lizenzen bereits angezeigt wurde.
     */
    private hasShowLicenceWarning: boolean;

    /**
     * Der ausgewählte Template-Style
     */
    templateStyle: KnockoutComputed<string>;

    /**
     * Das Icon der Anwendung
     */
    private _iconUrl: KnockoutObservable<string>;

    /**
     * Das Icon der Anwendung
     */
    iconUrl: KnockoutComputed<string>;

    /**
     * Ein Tooltip
     */
    tooltip: KnockoutObservable<string>;

    /**
     * Die aktuelle Konfiguration
     */
    configuration: IConfigurationDTO;

    /**
     * Das MenuSprite
     */
    menuSprite: any;

    /**
     * Gibt an, ob das Laden des MenuSprites abgeschlossen ist
     */
    menuSpriteLoaded: JQueryDeferred<boolean>;

    /**
     * Informationen über den derzeitigen Mandanten
     */
    principal: Principal;

    get user(): User {
        return this.userProvider.getUser();
    }

    /**
     * Beinhaltet alle deployten Module
     */
    deployedModules: KnockoutObservable<any>;

    /**
     * Beinhaltet den Änderungszeitpunkt von PDApplication
     */
    pdApplicationChangedTime: KnockoutObservable<any>;

    /**
     * Beinhaltet Informationen darüber, welche Module lizensiert sind
     */
    licensedModules: KnockoutObservable<any>;

    /**
     * Gibt an, ob das Modul "RAD" lizensiert ist.
     */
    isRADlicensed: KnockoutComputed<boolean>;

    /**
     * Beinhaltet Informationen darüber, für welche Module die Lizenz abgelaufen ist
     */
    expiredModules: KnockoutObservable<any>;

    /**
     * Beinhaltet alle verfügbaren Module
     */
    modules: KnockoutObservable<any>;

    /**
     * Verwies auf den aktuellen ListViewContext
     */
    currentListViewContext: KnockoutObservable<any>;

    /**
     * Verwies auf den aktuellen SummaryViewContext
     */
    currentSummaryViewContext: KnockoutObservable<any>;

    /**
     * Verwies auf den aktuellen DetailViewContext
     */
    currentDetailViewContext: KnockoutObservable<DetailViewContext>;

    /**
     * Standard PersistenceContext
     */
    defaultPersistenceContextId: KnockoutObservable<any>;

    /**
     * Eine Liste der definierten UIInterceptoren
     */
    uiInterceptors: Array<UIInterceptor>

    /**
     * Gibt an, ob die SPA-Anwendung ordnungsgemäß gestartet wurde
     */
    isInitialised: boolean

    /**
     * Verwaltet die Webservice-Adresse
     */
    serverUrl: string;

    /**
     * URL für den RAD-Environment-Service
     */
    radEnvironmentServiceUrl: string;

    /**
     * URL für den Template-Services des RAD-Environments
     */
    radEnvironmentTemplateHandler: string;

    /**
     * URL für den Data-Services des RAD-Environments
     */
    radEnvironmentDataHandler: string;

    /**
     * URL für den Icon-Services des RAD-Environments
     */
    radEnvironmentMediumHandler: string;

    /**
     * URL für den View-Extension-Serivces des RAD-Environments
     */
    radEnvironmentViewExtensionHandler: string;

    /**
     * Beinhaltet das Connection-Token für diese Instanz
     */
    token: string;

    xcsrfToken: string;

    /**
     * Beinhaltet den SSO UserName
     */
    ssoUserName: string;

    /**
     * Beinhaltet die Profiler-Instanz
     */
    profiler;

    /**
     * Zeigt ob das Bundling eingeschaltet ist.
     */
    BundlingEnabled: boolean;

    IsReducedView: boolean;

    private modalWindowOpenCounter: number;
    private showingUnauthorizedRequestCallback: boolean;
    private showingNoConnectionToServerCallback: boolean;

    /**
     * Gibt an, ob eine Database-Exception (wenn vorhanden) vom User akzeptiert wurde
     * (in der Regel durch Bestätigen einer MessageBox)
     */
    private databaseExceptionExcepted: boolean = true;

    constructor(
        @inject("Loader") private loader: ILoader,
        @inject("ResourceManager") private resourceManager: IResourceManager,
        @inject("NavigationService") private navigationService: INavigationService,
        @inject("KeepAliveHandler") private keepAliveHandler: IKeepAliveHandler,
        @inject("UserProvider") private userProvider: IUserProvider,
        @inject("RADEnvironmentProxy") private proxy: IRADEnvironmentProxy,
    ) {
        this.appName = ko.observable();
        this.buildVersion = ko.observable();
        this.description = ko.observable();
        this.ergName = ko.observable();
        this.rootLayoutVM = null;
        this.isSystemLocked = ko.observable(false);
        this.lastCacheUpdate = ko.observable();
        this.lastRightCacheUpdate = ko.observable();
        this.principalTypeName = ko.observable();
        this.systemLockMessage = ko.observable();
        this.isForceLogout = ko.observable(false);
        this.isChangePrincipalEnabled = ko.observable(true);
        this._templateStyle = ko.observable(MODEL.Style + '_HTML5');
        this.hasShowLicenceWarning = false;
        this.templateStyle = ko.computed({
            read: () => {
                let value = this._templateStyle();
                $('html').addClass('styleTemplate-' + value.replace('_HTML5', ''));
                return value.replace('_HTML5', '');
            },
            write: (value) => {
                let originalValue = value;
                value = value + '_HTML5';

                // BugFix für FF: veränderliches Token in URL sorgt dafür, dass der Stil auf jeden Fall neu geladen wird

                let oldSource = $('#styles').attr('href');
                let newSource = this.radEnvironmentTemplateHandler + '?TemplateName=' + value + '&StylePath=Styles&_ts=' + (new Date().getTime());
                $('html').removeClass('styleTemplate-' + this._templateStyle().replace('_HTML5', ''));
                $('html').addClass('styleTemplate-' + value.replace('_HTML5', ''));

                this._templateStyle(value);

                this.loader.while(null, () => {
                    // BugFix für IE: Beim Anwenden des Styles kam es zu Phantom-Hover-Effekten; hierdurch werden diese verhindert
                    document.body.style.pointerEvents = 'none';
                    $('#styles').attr('href', newSource);

                    // Wir wollen ein Callback für das Ereignis "CSS geladen" haben; leider funktioniert dies nicht browser-übergreifend
                    // HACK:
                    // Der einfachste Trick ist: Einfach ein Bild erzeugen, das die CSS-Daten als Quelle hat; wenn die Daten ankommen, merkt der
                    // Browser, dass sie ungültig sind und löst onerror aus. Zu dem Zeitpunkt sollte das CSS definitiv im Style gelandet und verarbeitet sein.
                    let img = new Image();
                    img.onerror = () => {
                        window.setTimeout(() => {
                            // Der IE aktualisiert einige Layouts nicht nach dem Stil-Update;
                            // dies forciert einige Re-Layouts
                            $(document.body).css('display', 'none').css('display', '');

                            // Der FF aktualisiert einige SVG-Farbverläufe nicht nach dem Stil-Update
                            $("svg [fill]").each(function () {
                                let fill = $(this).attr("fill");
                                $(this).attr("fill", "").attr("fill", fill);
                            });

                            // Dies ermöglicht ggf. komponentenspezifische Fixes (z.B. mittels Reinitialisierung/Rendering) für das Stil-Update
                            Messenger.createMessageAndSend('TemplateStyleChangedMessage', {style: originalValue});

                            document.body.style.pointerEvents = ''; // Maus-Ereignisse wieder erlauben
                        }, 10);
                    };
                    img.src = newSource;
                });
            }
        });
        this._iconUrl = ko.observable($('#favicon').attr('href'));
        this.iconUrl = ko.computed({
            read: this._iconUrl,
            write: (value: any) => {
                value = this.templateStyle() + '_HTML5';

                let oldSource = $('#favicon').attr('href');
                let newSource = this.radEnvironmentTemplateHandler + '?TemplateName=' + value + '&ImageName=favicon.ico&_ts=' + Date.now();

                if (oldSource !== newSource) {
                    $('#favicon').attr('href', newSource);
                    this._iconUrl(newSource);
                }
            }
        });
        this.tooltip = ko.observable();
        this.configuration = null;
        this.menuSprite = null;
        this.menuSpriteLoaded = null;
        this.principal = null;
        this.deployedModules = ko.observable();
        this.pdApplicationChangedTime = ko.observable();
        this.licensedModules = ko.observable();
        this.isRADlicensed = ko.computed(() => {
            let licensedModules = this.licensedModules();

            if (!licensedModules) {
                return false;
            }

            return licensedModules.indexOf('RAD') !== -1;
        });
        this.expiredModules = ko.observable();
        this.modules = ko.observable();
        this.currentListViewContext = ko.observable(null);
        this.currentSummaryViewContext = ko.observable(null);
        this.currentDetailViewContext = ko.observable(null);
        this.defaultPersistenceContextId = ko.observable(null);
        this.uiInterceptors = [];
        this.isInitialised = false;

        let browserUrl = window.location.href;

        if (MODEL.StartViewName && MODEL.StartViewName != '') {
            const idxStartView = browserUrl.lastIndexOf('/' + MODEL.StartViewName);
            if (idxStartView > -1) {
                browserUrl = browserUrl.substring(0, idxStartView);
            }
        }

        if (browserUrl.indexOf('/', browserUrl.length - 1) == -1)
            browserUrl += "/";

        this.serverUrl = browserUrl + MODEL.HTMLClientBaseURL;

        this.radEnvironmentServiceUrl = MODEL.HTMLClientBaseURL + MODEL.RADEnvironmentServiceURL;
        this.radEnvironmentTemplateHandler = MODEL.HTMLClientBaseURL + MODEL.RADEnvironmentTemplateServiceURL;
        this.radEnvironmentDataHandler = MODEL.HTMLClientBaseURL + MODEL.RADEnvironmentDataServiceURL;
        this.radEnvironmentMediumHandler = MODEL.HTMLClientBaseURL + MODEL.RADEnvironmentMediumServiceURL;
        this.radEnvironmentViewExtensionHandler = MODEL.HTMLClientBaseURL + MODEL.RADEnvironmentViewExtensionServiceURL;

        this.token = MODEL.Token;
        this.xcsrfToken = MODEL.XCSRFToken;
        this.ssoUserName = MODEL.SSOUserName;
        this.profiler = null;
        this.BundlingEnabled = MODEL.HTMLClientBundling;

        this.modalWindowOpenCounter = 0;


        this.showingUnauthorizedRequestCallback = false;
        this.showingNoConnectionToServerCallback = false;
        this.IsReducedView = this.checkReducedViewParameter();
    } // end of constructor

    /**
     * URL für den View-Extension-Serivces des RAD-Environments
     * Verweist intern auf den ResourceManager welcher die aktuelle Sprache verwaltet
     */
    currentLanguage() {
        return ko.toJS(this.resourceManager.language.value);
    }

    /**
     * Beinhalte alle übersetzten standard ResourceItems für eine RAD-Anwendung
     * Verweist intern auf den ResourceManager welcher die Resource-Elemente verwaltet
     */
    resourceItemValues() {
        return ko.toJS(this.resourceManager.resources);
    }

    /**
     * Öffnet eine MessageBox
     */
    isModalWindowOpen() {
        return this.modalWindowOpenCounter > 0;
    }

    increaseModalWindowOpenCounter() {
        this.modalWindowOpenCounter++;
    }

    decreaseModalWindowOpenCounter() {
        this.modalWindowOpenCounter--;
    }

    showMessageBox(messageBoxViewModel: MessageBoxViewModelT, isNavigatable?: boolean) {
        isNavigatable = !!isNavigatable;

        // Die Erzeugung einer modalen MessageBox soll immer isoliert (also ohne Abhängigkeiten) zum NavigationService geschehen.
        // Somit bleibt beim Öffnen der MessageBox die aktive ViewFactory erhalten und wird nicht durch die MessageBox überschrieben.
        let messageBoxFactory = this.navigationService.loadViewFactoryIsolated('MessageBox', {
            viewModel: messageBoxViewModel
        }, true);

        if (messageBoxViewModel.window.isModal() && !isNavigatable) {
            this.increaseModalWindowOpenCounter();
        }

        let disposeBox = () => {
            if (!isNavigatable) {
                this.decreaseModalWindowOpenCounter();
            }

            this.navigationService.removeViewFactoryIsolated(messageBoxFactory);

            window.setTimeout(() => {
                if (!messageBoxViewModel.isDisposed) {
                    messageBoxViewModel.dispose();
                }
                messageBoxViewModel = null;
            }, 1); // dies sofort auszuführen kann registrierte close-Handler stören
        };

        let onClose;
        let focusedElementBefore = document.activeElement;
        if (messageBoxViewModel.window.onClose != null) {
            let originalOnClose = messageBoxViewModel.window.onClose;
            onClose = () => {
                originalOnClose();
                disposeBox();
                $(focusedElementBefore).focus();
            };
        } else {
            onClose = () => {
                disposeBox();
                $(focusedElementBefore).focus();
            };
        }
        messageBoxViewModel.window.onClose = onClose;
    }

    /**
     * Behandelt eine Datenbank-Exception
     */
    handleDatabaseException(): void {
        const MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');

        if (this.databaseExceptionExcepted) {
            this.databaseExceptionExcepted = false;
            const vm = MessageBoxViewModel.createWarningMessageBox(this.resourceManager.get('ADO_EXCEPTION_MESSAGE'),
                () => {
                    this.databaseExceptionExcepted = true;
                });

            this.showMessageBox(vm);
        }

        if (!Utilities.isNullOrUndefinedOrBlank(this.currentDetailViewContext())) {
            this.currentDetailViewContext().cancel();
        }
    }

    /**
     * Wird aufgerufen, nachdem die Anwendung gestartet wurde.
     * Hinweis: Die Function 'callback' muss zwingend aufgerufen werden.
     */
    onAfterStartup(callback: () => void) {
        let countInterceptors = this.uiInterceptors.length;

        if (countInterceptors === 0) {
            callback();
            return;
        }

        let continueCounter = 0;

        for (let i = 0; i < this.uiInterceptors.length; i++) {
            this.uiInterceptors[i].afterStartup(() => {
                if (++continueCounter == countInterceptors) {
                    callback();
                }
            });
        }
    }

    /**
     * Wird aufgerufen, nachdem ein Benutzer angemeldet wurde.
     * Hinweis: Die Function 'callback' muss zwingend aufgerufen werden.
     */
    onAfterLogin(callback: () => void) {
        let countInterceptors = this.uiInterceptors.length;

        if (countInterceptors === 0) {
            callback();
            return;
        }

        let continueCounter = 0;

        for (let i = 0; i < this.uiInterceptors.length; i++) {
            this.uiInterceptors[i].afterLogin(() => {
                if (++continueCounter == countInterceptors) {
                    callback();
                }
            });
        }
    }

    /**
     * Wird aufgerufen, bevor das Rootlayout geladen oder aktualisiert wird.
     */
    onBeforeLoadRootLayout() {
        for (let i = 0; i < this.uiInterceptors.length; i++) {
            this.uiInterceptors[i].beforeLoadRootLayout();
        }
    }

    /**
     * Wird aufgerufen, sobald das Rootlayout geladen wurde.
     */
    onAfterLoadRootLayout() {

        // das RootLayoutViewModel herranziehen, wenn möglich
        let wireframeDOM = $('[data-view=Wireframe]')[0];
        if (wireframeDOM) {
            let koData = ko.dataFor(wireframeDOM);
            if (koData && koData.viewModel) {
                this.rootLayoutVM = koData.viewModel;
            }
        }

        this.showExpiredModulesMessage();

        for (let i = 0; i < this.uiInterceptors.length; i++) {
            this.uiInterceptors[i].afterLoadRootLayout();
        }
    }

    /**
     * Wird aufgerufen bevor der Menübaum geladen wird.
     */
    onBeforeLoadMenuTree(rootNode) {
        for (let i = 0; i < this.uiInterceptors.length; i++) {
            this.uiInterceptors[i].beforeLoadMenuTree(rootNode);
        }
    }

    /**
     * Wird aufgerufen bevor das UserDropdownMenu geladen wird.
     */
    onBeforeLoadUserDropdownMenu(menuItems: IDropdownMenuItem[]) {
        for (let i = 0; i < this.uiInterceptors.length; i++) {
            this.uiInterceptors[i].beforeLoadUserDropdownMenu(menuItems);
        }
    }

    /**
     * Wird aufgerufen bevor der Navigationsbaum geladen wird.
     */
    onBeforeLoadNavigationTree(rootNode) {
        for (let i = 0; i < this.uiInterceptors.length; i++) {
            this.uiInterceptors[i].beforeLoadNavigationTree(rootNode);
        }
    }

    /**
     * Methode, welche nach einem Login die Login-Informationen setzt
     */
    afterLogin(loginDTO, principalDTO) {

        this.onAfterLogin(() => {
            this.showBackground();

            this.principal.setPrincipalInfo(principalDTO);
            this.userProvider.getUser().setLoginInfo(loginDTO);
            this.lastRightCacheUpdate(loginDTO.rightCacheCreated);

            this.keepAliveHandler.startWatchDog();

            // Flag wieder entfernen
            StorageHelper.remove('ForceLogout');
            StorageHelper.removeGlobal('ForceLogout');

            const user = loginDTO.clientInfo.user;

            if (!user.needsTwoFaRegistration)
                this.navigationService.navigateToRedirectingAddress();

            this.navigationService.handleUserInputAfterLogin(user.changePasswordWithNextLogin, user.needsTwoFaRegistration);

            if (!MODEL.IsLinkAllowed) {
                let MessageBoxViewModel = ComponentManager.getViewModel <typeof MessageBoxViewModelT>('MessageBoxViewModel');
                this.showMessageBox(MessageBoxViewModel.createWarningMessageBox(this.resourceManager.get('DEEPLINK_FORBIDDEN')));
            }
        });
    }

    private showBackground() {
        $('body').addClass('background');
    }

    beforeLogout() {
        if (this.keepAliveHandler != null && this.keepAliveHandler.stopWatchDog !== undefined)
            this.keepAliveHandler.stopWatchDog();
    }

    /**
     * Methode, welche nach dem Logout zum Login navigiert
     */
    afterLogout() {
        const user = this.userProvider.getUser();

        if (user != null && user.isLoggedIn !== undefined)
            user.isLoggedIn(false);

        // Im Local Storage ein Flag "ForceLogout" setzen, damit die anderen Tabs informiert werden, dass sie sich ausloggen sollen.
        if (user != null && (user.pdoId() === -1 || user.pdoId() === null))
            StorageHelper.setGlobal('ForceLogout', 'ForceLogout');
        else
            StorageHelper.set('ForceLogout', 'ForceLogout');

        if (this.navigationService != null && this.navigationService.reloadAndLogout !== undefined) {
            //Nun wird der Client neugeladen..
            this.navigationService.reloadAndLogout();
        }
    }

    /**
     * Gibt an, ob eine genannte Systemvariable gesetzt und auf True gestellt ist
     */
    isSystemParamSetAndTrue(name: string) {
        if (name in this.configuration.systemProperties) {
            let param = this.configuration.systemProperties[name];
            return (param as any) === true || param === 'True' || param === 'true';
        }

        return false;
    }

    /**
     * Liefert den Wert einer Systemvariablen
     */
    getSystemParam(name: string, parsingFunc?: (param: any) => any): any {
        if (!this.configuration || !this.configuration.systemProperties)
            return null;

        if (name in this.configuration.systemProperties) {
            let param = this.configuration.systemProperties[name];

            if (parsingFunc) {
                param = parsingFunc(param);
            }

            return param;
        }

        return null;
    }

    /**
     * Registrert Keyboard-Events
     */
    manageKeyboard() {
        $(document).bind('keyup', (event) => {
            let templateStyle = this.templateStyle();

            if (event.keyCode === kendo.keys.F10) {
                this.templateStyle(null);
                this.templateStyle(templateStyle);
            }

            if (event.keyCode === kendo.keys.F2) {
                this.toggleProfiler();
            }
        });

        // unterbindet das markieren des gesamten textinhalts in der anwendung
        // davon ausgeschlossen sind jedoch inputfelder
        Mousetrap.bind(['mod+a'], (e) => {
            return false;
        });
    }

    /**
     * Initialisiert den Profiler
     */
    toggleProfiler() {
        if (this.BundlingEnabled)
            return;

        if (this.profiler == null) {
            this.profiler = new Profiler();
            this.navigationService.loadViewFactoryIsolated('Debug', null, true);
        } else {
            this.profiler = null;
            this.navigationService.cleanRegion('Debug');
        }
    }

    /**
     * Führt ein Cache-Update durch
     */
    updateCache(): JQueryPromise<void> {
        const user = this.userProvider.getUser();

        return this.proxy.updateClientCache({
            level: 'Full'
        }).then((clientUpdate) => {
            if (Config.clientCache.showLoaderOnUpdate)
                this.loader.show();

            if (clientUpdate.clientInfo != null) {
                user.setClientInfo(clientUpdate.clientInfo);
            }

            if (clientUpdate.applicationMenus != null) {
                if (clientUpdate.applicationMenus.userNavigationTree != null) {
                    this.onBeforeLoadNavigationTree(clientUpdate.applicationMenus.userNavigationTree);
                }

                if (clientUpdate.applicationMenus.userMenuTree != null) {
                    this.onBeforeLoadMenuTree(clientUpdate.applicationMenus.userMenuTree);
                }

                user.menus(clientUpdate.applicationMenus);
            }

            if (clientUpdate.configuration != null) {
                this.configuration = clientUpdate.configuration;
            }

            if (clientUpdate.principalInfo != null) {
                this.principal.setPrincipalInfo(clientUpdate.principalInfo);
            }

            this.onBeforeLoadRootLayout();
            Messenger.createMessageAndSend('RefreshRootLayoutMessage');
            this.onAfterLoadRootLayout();

            Messenger.createMessageAndSend('CacheUpdatedMessage');

            if (Config.clientCache.showLoaderOnUpdate) {
                this.loader.hide();
            }

            return;
        });
    }

    /**
     * Führt ein Update der ResouceItems durch
     */
    updateAllResourceItems(): JQueryPromise<void> {
        return this.resourceManager.loadLanguageArtfacts(null);
    }

    unauthorizedRequestCallback() {
        if (this.showingUnauthorizedRequestCallback === true) {
            return;
        }

        this.isForceLogout(true);
        this.showingUnauthorizedRequestCallback = true;

        let MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');

        //So verhindert wir, dass Loader hängen bleiben und kein Logout stattfindet
        this.loader.removeAll();

        const msgVM = MessageBoxViewModel.createWarningMessageBox(this.resourceManager.get('SESSION_ERROR'), () => {
            const user = this.userProvider.getUser();

            this.proxy.autoLogout({
                clientInfoID: user.clientInfoID()
            }).always(() => this.afterLogout());
        }, {});

        this.showMessageBox(msgVM);
    }

    noConnectionToServerCallback() { //Duplication from unauthorizedRequestCallback!!!
        if (this.showingNoConnectionToServerCallback === true) {
            return;
        }
        this.isForceLogout(true);
        this.showingNoConnectionToServerCallback = true;

        let MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');

        //So verhindert wir, dass Loader hängen bleiben und kein Logout stattfindet
        this.loader.removeAll();

        let msg = this.resourceManager.get('COMMUNICATION_ERROR');
        if (msg === 'COMMUNICATION_ERROR')
            msg = 'The service is temporarily unavailable. Please try again later.';

        const msgVM = MessageBoxViewModel.createWarningMessageBox(msg, () => {
            const user = this.userProvider.getUser();

            this.proxy.autoLogout({
                clientInfoID: user.clientInfoID()
            }).always(() => this.afterLogout());
        }, {});

        this.showMessageBox(msgVM);
    }

    /**
     * Instanziiert alle geladenen UIInterceptoren
     */
    loadUIInterceptors() {
        this.uiInterceptors = [];

        let uiInterceptors = ComponentManager.getUIInterceptors();

        for (let i = 0; i < uiInterceptors.length; i++) {
            this.uiInterceptors.push(new uiInterceptors[i]());
        }
    }

    /**
     * Initialisiert die Anwendung
     */
    init() {
        this.loadUIInterceptors();

        this.proxy.getApplication().then((applicationDTO) => {
            if (applicationDTO.status !== 'Successfull') {
                let MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');
                this.showMessageBox(MessageBoxViewModel.createWarningMessageBox(applicationDTO.errorMessage));
                return;
            }
            this.configuration = applicationDTO.configuration;

            this.pdApplicationChangedTime(applicationDTO.pdApplicationChangedTime);
            this.deployedModules(applicationDTO.deployedModules);
            this.manageKeyboard();
            this.description(applicationDTO.description);
            this.ergName(applicationDTO.ergName);
            this.expiredModules(applicationDTO.expiredModules);
            this.iconUrl(applicationDTO.iconUrl);
            this.isSystemLocked(applicationDTO.isSystemLocked);
            this.lastCacheUpdate(applicationDTO.lastCacheUpdate);
            this.licensedModules(applicationDTO.licensedModules);
            this.modules(applicationDTO.modules);
            this.appName(applicationDTO.name);
            this.principalTypeName(applicationDTO.principalTypeName);
            this.systemLockMessage(applicationDTO.systemLockMessage);
            this.tooltip(applicationDTO.tooltip);
            this.buildVersion(applicationDTO.buildVersion);

            this.principal = new Principal();

            let reloadWithoutCache = StorageHelper.checkLocalStorageRefresh();

            if (reloadWithoutCache === true) {
                window.location.replace(window.location.href);
            } else {
                this.resourceManager.init(this.extractLanguageCode()).then(() => {
                    this.onAfterStartup(() => {
                        this.isInitialised = true;

                        this.navigationService.init();
                    });
                });
            }
        }).fail((param) => {
            console.log("An error has occurred while getting the application. Please contact the administrator.");
        });
    }

    private extractLanguageCode(): string {
        let currentLang = navigator.language.toUpperCase();

        // Es gibt zwei verschiedene Darstellungen der Sprache: 'de' und 'de-DE'. Das CoreFrame kann an der Stelle nur das 'DE' verarbeiten um die
        // ResourceItems anzuzeigen
        // Fallback-Language: Englisch, da die Wahrscheinlichkeit höher ist, dass jemand, welcher eine abweichende Sprache eingestellt hat, mehr mit Englisch
        // als Deutsch anfangen kann!
        const dashIdx = currentLang.indexOf('-');
        if (dashIdx > 0)
            currentLang = currentLang.substr(0, dashIdx);

        return currentLang;
    }

    /**
     * Liefert zu einem Mandanten-Filter-Modus den entsprechend Client-Wert.
     */
    getServerValueForPrincipalFilterMode(keyOrValue: string) {
        return PrincipalHelper.getServerFilterMode(keyOrValue);
    }

    /**
     * Liefert zu einem Mandanten-Filter-Modus den entsprechend Server-Wert.
     */
    getClientValueForPrincipalFilterMode(keyOrValue: string) {
        return PrincipalHelper.getClientFilterMode(keyOrValue);
    }

    private showExpiredModulesMessage() {
        let MessageBoxViewModel, expiredText, expiredModulesText, msgVM;

        if (!this.hasShowLicenceWarning && this.expiredModules() && this.expiredModules().length > 0) {
            MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');

            expiredText = this.resourceManager.get('LICENSE_EXPIRED_MODULES');
            expiredModulesText = this.expiredModules().join(', ');

            msgVM = MessageBoxViewModel.createWarningMessageBox(expiredText + ' ' + expiredModulesText, {});
            this.showMessageBox(msgVM);

            this.hasShowLicenceWarning = true;
        }
    }

    private checkReducedViewParameter(): boolean {

        if (MODEL.ReduceUI && MODEL.ReduceUI === true)
            return true;

        let searchParams = new URLSearchParams(window.location.search);
        if (!searchParams.has('reduced'))
            return false;

        return searchParams.get('reduced') == 'true';
    }
}